/**
 * Utility for managing redirect paths throughout the authentication flow.
 * Persists redirect information through signup and email verification processes.
 */

const REDIRECT_STORAGE_KEY = 'pendingRedirect';

export interface RedirectInfo {
  path: string;
  query: string;
  timestamp: number;
}

/**
 * Save redirect information to localStorage
 */
export function saveRedirectInfo(path: string, query: string = ''): void {
  const redirectInfo: RedirectInfo = {
    path,
    query,
    timestamp: Date.now(),
  };
  localStorage.setItem(REDIRECT_STORAGE_KEY, JSON.stringify(redirectInfo));
}

/**
 * Get saved redirect information from localStorage
 * Returns null if no redirect is saved or if it's expired (older than 24 hours)
 */
export function getRedirectInfo(): RedirectInfo | null {
  const stored = localStorage.getItem(REDIRECT_STORAGE_KEY);
  if (!stored) {
    return null;
  }

  try {
    const redirectInfo: RedirectInfo = JSON.parse(stored);

    // Expire redirects older than 24 hours
    const twentyFourHours = 24 * 60 * 60 * 1000;
    const threeDays = 3 * twentyFourHours;
    if (Date.now() - redirectInfo.timestamp > threeDays) {
      clearRedirectInfo();
      return null;
    }

    return redirectInfo;
  } catch (error) {
    console.error('Failed to parse redirect info:', error);
    clearRedirectInfo();
    return null;
  }
}

/**
 * Get the full redirect URL (path + query)
 */
export function getRedirectUrl(): string | null {
  const redirectInfo = getRedirectInfo();
  if (!redirectInfo) {
    return null;
  }

  return `${redirectInfo.path}${redirectInfo.query}`;
}

/**
 * Clear saved redirect information
 */
export function clearRedirectInfo(): void {
  localStorage.removeItem(REDIRECT_STORAGE_KEY);
}

/**
 * Check if there is a pending redirect
 */
export function hasPendingRedirect(): boolean {
  return getRedirectInfo() !== null;
}
